import React, { useState } from "react"
import { Box, Stack, Button, FormControl, useTheme } from "@mui/material"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import ViewButton from "src/components/table/ViewButton"
import SearchTextField from "src/components/table/SearchTextField"
import TuneIcon from "@mui/icons-material/Tune"
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded"
import { Refresh as RefreshIcon } from "@mui/icons-material"
import { useResponsive } from "src/utils/useResponsive"
import DataSourcesFilterModal from "./DataSourcesFilterModal"
import { styled } from "@mui/material/styles"

export interface StatusOption {
  label: string
  value: string[]
}

export interface DataSourceOption {
  label: string
  value: string
}

export interface DataSourcesFilterProps {
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  statusFilter: StatusOption[]
  onStatusFilterChange: (statuses: StatusOption[]) => void
  statusOptions: StatusOption[]
  dataSourceFilter: DataSourceOption | undefined
  onDataSourceFilterChange: (dataSource: DataSourceOption) => void
  dataSourceOptions: DataSourceOption[]
  onRefresh: () => void
  onUploadClick: () => void
}

const StyledIconButton = styled(ViewButton)(({ theme }) => ({
  borderRadius: "6px",
  backgroundColor: theme.palette.common.white,
  border: `1px solid #E0E0E0`,
  height: "auto",
  "&:hover": {
    backgroundColor: theme.palette.common.white,
    color: "#898989",
    boxShadow: `1px 1px 6px #898989`
  }
}))

const DataSourcesFilter: React.FC<DataSourcesFilterProps> = ({
  searchQuery,
  onSearchQueryChange,
  statusFilter,
  onStatusFilterChange,
  statusOptions,
  dataSourceFilter,
  onDataSourceFilterChange,
  dataSourceOptions,
  onRefresh,
  onUploadClick
}) => {
  const { isMobile } = useResponsive()
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const { palette } = useTheme()
  return (
    <Box
      sx={{
        backgroundColor: palette.primary.light,
        boxShadow: "0px 2px 10px #0719391A",
        p: { xs: 2, md: 6 },
        pb: 4
      }}
    >
      <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent="space-between">
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
          <Button variant="contained" color="primary" onClick={onUploadClick} fullWidth={isMobile}>
            Upload sources
          </Button>

          {isMobile && (
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={() => setFilterModalOpen(true)}
              sx={{
                borderColor: "#E0E0E0",
                minWidth: "auto",
                px: 2
              }}
              fullWidth={isMobile}
            >
              Filters
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={isMobile ? { width: "100%" } : undefined}>
            <FormControl fullWidth={isMobile}>
              <SearchTextField
                value={searchQuery}
                onChange={onSearchQueryChange}
                onClear={() => onSearchQueryChange("")}
              />
            </FormControl>
          </Box>
          {!isMobile && (
            <>
              <Box sx={{ width: "200px" }}>
                <CustomAutocomplete<StatusOption>
                  value={statusFilter}
                  options={statusOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.label === value.label}
                  onChange={(e, val) => {
                    onStatusFilterChange(val as StatusOption[])
                  }}
                  label="Status"
                  size="small"
                  multiple
                  hideInputText
                />
              </Box>
              <Box sx={{ width: "250px" }}>
                <CustomAutocomplete<DataSourceOption>
                  value={dataSourceFilter}
                  options={dataSourceOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  onChange={(e, val) => {
                    onDataSourceFilterChange(val as DataSourceOption)
                  }}
                  label="Knowledge Base"
                  size="small"
                  disableClearable
                />
              </Box>
              <StyledIconButton onClick={onRefresh}>
                <RefreshIcon />
              </StyledIconButton>
            </>
          )}
        </Stack>
      </Stack>

      {/* Mobile Filter Modal */}
      <DataSourcesFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        statusOptions={statusOptions}
        dataSourceFilter={dataSourceFilter}
        onDataSourceFilterChange={onDataSourceFilterChange}
        dataSourceOptions={dataSourceOptions}
      />
    </Box>
  )
}

export default DataSourcesFilter
